import { useOptimizationGroupDataUpdate, useSnackbar, useTokenInfo } from '@e2m-customer-portal/hooks';
import { optimizationWhitelist, UserRole } from '@e2m-customer-portal/shared';
import {
  Drawer,
  DrawerButtons,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  InfoTooltip,
  NumberInput,
  ValidatedContainer,
} from '@e2m-customer-portal/ui';
import {
  AssetType,
  EegSubsidyParametersType,
  OptimizationParameterValue,
  PVSystemDataType,
} from 'domains/optimization-group/types';
import { equals } from 'ramda';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface PVSidePanelProps {
  data: PVSystemDataType;
  identifier?: string;
  id: string;
  assetType: AssetType;
  open: boolean;
  onClose: () => void;
}

export function PVSidePanel({ data, identifier, id, assetType, open, onClose }: Readonly<PVSidePanelProps>) {
  const [t] = useTranslation();
  const { roles } = useTokenInfo();
  const isVPPOperator = roles.has(UserRole.VPPOperator);
  const [isValid, setIsValid] = useState(false);
  const { showError, showSuccess } = useSnackbar();
  const [isSaving, setIsSaving] = useState(false);
  const [update] = useOptimizationGroupDataUpdate();

  const [updatedData, setUpdatedData] = useState<PVSystemDataType>({ ...data });
  const [changes, setChanges] = useState<Map<string, OptimizationParameterValue>>(new Map());
  const [eegSubsidyParameters, setEegSubsidyParameters] = useState<EegSubsidyParametersType | undefined>(
    data.eeg_subsidy_parameters,
  );

  const readOnly = (parameter: string) => !isVPPOperator && !!parameter && !optimizationWhitelist[parameter];

  const updateValues = () => {
    if (isValid) {
      setIsSaving(true);
      const mutationBody = identifier
        ? JSON.stringify({
            asset_type: assetType,
            pv: { [identifier]: Object.fromEntries(changes) },
          })
        : JSON.stringify({
            asset_type: assetType,
            pv: Object.fromEntries(changes),
          });

      update(id, mutationBody)
        .then((res) => {
          if (res.data?.OptimizationGroupDataUpdate.json) {
            showSuccess(t('COPT_EDIT_SUCCESS_MESSAGE'));
          }
        })
        .catch((error) => {
          showError(error);
        })
        .finally(() => {
          setIsSaving(false);
          setChanges(new Map());
          onClose();
        });
    }
  };

  const resetValues = () => {
    setUpdatedData({ ...data });
    setEegSubsidyParameters(data.eeg_subsidy_parameters);
    setChanges(new Map());
  };

  useEffect(() => {
    if (open) {
      setUpdatedData({ ...data });
    }
  }, [open]);

  useEffect(() => {
    if (eegSubsidyParameters && !equals(eegSubsidyParameters, data.eeg_subsidy_parameters)) {
      setChanges(changes.set('eeg_subsidy_parameters', eegSubsidyParameters));
    }
  }, [eegSubsidyParameters]);

  return (
    <Drawer open={open} onClose={onClose} testId="pv-side-panel" disableEnforceFocus>
      <DrawerHeader cancelLabel={t('SHARED_CANCEL')} noDivider></DrawerHeader>
      <DrawerContent className="pt-2 *:pt-4" unknownErrorLabel={t('SHARED_ERROR_UNKNOWN')}>
        {updatedData.alias ? (
          <h3 className="m-0 pb-4 flex flex-col whitespace-nowrap xl:col-span-2">
            <span className="text-xs font-normal text-grey-300">{t('COPT_PV_TITLE')}</span>
            <span className="inline-flex items-center pt-1 text-lg font-medium">{updatedData.alias}</span>
          </h3>
        ) : (
          <span className="inline-flex items-center pt-1 pb-4 text-lg font-medium">{t('COPT_PV_TITLE')}</span>
        )}
        <div className="grid grid-cols-2 gap-6 *:pb-2 m-0 border-solid border-0 border-t border-b border-divider ">
          {updatedData?.device_number !== undefined && (
            <div className="*:m-0 *:pt-1">
              <p className="text-sm text-grey-300"> {`${t('COPT_PV_DEVICE_NUMBER')} `}</p>
              <p className="text-sm font-bold">{updatedData.device_number}</p>
            </div>
          )}
          {updatedData?.forecast_id !== undefined && (
            <div className="*:m-0 *:pt-1">
              <p className="text-sm text-grey-300"> {`${t('COPT_PV_FORECAST_ID')} `}</p>
              <p className="text-sm font-bold">{updatedData.forecast_id}</p>
            </div>
          )}
        </div>

        <ValidatedContainer validate="on" onValidated={setIsValid}>
          {updatedData.always_available !== undefined && (
            <label className="flex items-center text-sm font-light mr-4 xl:col-span-2">
              <input
                type="checkbox"
                checked={!!updatedData.always_available}
                disabled={readOnly('always_available')}
                onChange={() => {
                  const always_available = !updatedData.always_available;
                  setUpdatedData({ ...updatedData, always_available });
                  setChanges(changes.set('always_available', always_available));
                }}
                className="w-4 h-4 mr-2 accent-green-200"
              />
              {t('COPT_PV_ALWAYS_AVAILABLE')}
              <InfoTooltip field="always_available" text={t('COPT_PV_ALWAYS_AVAILABLE_TOOLTIP')} />
            </label>
          )}
          {updatedData.installed_power !== undefined && (
            <div className="xl:col-span-2">
              <NumberInput
                value={updatedData.installed_power === null ? null : updatedData.installed_power * 1000}
                required
                name="installed_power"
                label={t('COPT_PV_INSTALLED_POWER')}
                onChange={(value: number) => {
                  const installed_power = value != null && !Number.isNaN(value) ? value / 1000 : null;
                  setUpdatedData({
                    ...updatedData,
                    installed_power,
                  });
                  setChanges(changes.set('installed_power', installed_power));
                }}
                disabled={readOnly('installed_power')}
                notNegative
                endAdornment={<InfoTooltip field="installed_power" objectType="pv" />}
              />
            </div>
          )}
          {updatedData.accepted_power_deviation !== undefined && (
            <div className="xl:col-span-2">
              <NumberInput
                value={
                  updatedData.accepted_power_deviation === null
                    ? null
                    : Math.round(updatedData.accepted_power_deviation * 100)
                }
                required
                name="accepted_power_deviation"
                label={t('COPT_PV_ACCEPTED_POWER_DEVIATION')}
                onChange={(value: number) => {
                  const accepted_power_deviation =
                    value != null && !Number.isNaN(value) ? Math.round(value) / 100 : null;
                  setUpdatedData({
                    ...updatedData,
                    accepted_power_deviation,
                  });
                  setChanges(changes.set('accepted_power_deviation', accepted_power_deviation));
                }}
                disabled={readOnly('accepted_power_deviation')}
                notNegative
                endAdornment={<InfoTooltip field="accepted_power_deviation" objectType="pv" />}
              />
            </div>
          )}
          {eegSubsidyParameters && (
            <>
              <h4 className="m-0 pt-3 text-sm font-semibold ">{t('COPT_PV_EEG_SUBSIDY_PARAMETERS_TITLE')}</h4>
              {eegSubsidyParameters.eeg_auction_value !== undefined && (
                <NumberInput
                  value={
                    eegSubsidyParameters.eeg_auction_value === null ? null : eegSubsidyParameters.eeg_auction_value
                  }
                  required
                  name="eegSubsidyParameters.eeg_auction_value"
                  label={t('COPT_PV_EEG_SUBSIDY_PARAMETERS_EEG_AUCTION_VALUE')}
                  onChange={(value: number) => {
                    setEegSubsidyParameters({
                      ...eegSubsidyParameters,
                      eeg_auction_value: value != null && !Number.isNaN(value) ? value : null,
                    });
                  }}
                  disabled={readOnly('eeg_auction_value')}
                  notNegative
                  endAdornment={<InfoTooltip field="eeg_auction_value" objectType="pv_eeg_subsidy_parameters" />}
                />
              )}
              {eegSubsidyParameters.negative_hours_threshold !== undefined && (
                <NumberInput
                  value={
                    eegSubsidyParameters.negative_hours_threshold === null
                      ? null
                      : eegSubsidyParameters.negative_hours_threshold
                  }
                  required
                  name="eegSubsidyParameters.negative_hours_threshold"
                  label={t('COPT_PV_EEG_SUBSIDY_PARAMETERS_NEGATIVE_HOURS_THRESHOLD')}
                  onChange={(value: number) => {
                    setEegSubsidyParameters({
                      ...eegSubsidyParameters,
                      negative_hours_threshold: value != null && !Number.isNaN(value) ? value : null,
                    });
                  }}
                  disabled={readOnly('negative_hours_threshold')}
                  notNegative
                  endAdornment={<InfoTooltip field="negative_hours_threshold" objectType="pv_eeg_subsidy_parameters" />}
                />
              )}
            </>
          )}
        </ValidatedContainer>
      </DrawerContent>

      <DrawerFooter testId="forecast-assistant-footer">
        <DrawerButtons
          onCancel={() => {
            resetValues();
            onClose();
          }}
          onSave={updateValues}
          isLoading={isSaving}
          disableSave={!isValid || isSaving || !isVPPOperator}
          cancelLabel={t('SHARED_CANCEL')}
          saveLabel={t('SHARED_SUBMIT')}
        />
      </DrawerFooter>
    </Drawer>
  );
}
